import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

// The standard action button (docs/specs/DESIGN-SYSTEM.md §4). Built on the
// existing `.btn-*` classes in src/index.css so it matches unconverted pages.
// `.btn` floors height at 56px and `.btn-sm` at 44px (min-height in CSS), so
// both clear the 44px touch floor. `tap-target-y` is added as the guard-visible
// floor, but note it sets min-height: 44px on coarse pointers, which LOWERS
// `.btn`'s 56px md floor to 44px. Harmless today (py-4 + text-base still gives
// 56px); a caller that shrinks the padding gets 44px on touch, not 56px.

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'md' | 'sm'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Disables the button, sets aria-busy and shows a spinner in place of `icon`. */
  loading?: boolean
  fullWidth?: boolean
  /** A lucide element, shown before the label. */
  icon?: ReactNode
  children?: ReactNode
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, fullWidth = false, icon, className, type = 'button', disabled, children, ...rest },
  ref,
) {
  const cls = [
    VARIANT[variant],
    size === 'sm' ? 'btn-sm' : '',
    'tap-target-y',
    fullWidth ? 'w-full' : '',
    className ?? '',
  ].filter(Boolean).join(' ')
  return (
    <button
      ref={ref}
      type={type}
      className={cls}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  )
})

export default Button
